import { useMemo } from 'react';
import {
  useGetCollectionTicketClaims,
  type IssuedTicketFindResultDTO,
} from '@/api/trafikportalen';
import {
  canCancelTicket,
  periodBlocksManualTicketCancel,
  ticketComesFromPeriod,
} from '@/helpers/ticket';

export interface PeriodTicketCancelState {
  locked: boolean;
  explain: boolean;
  pending: boolean;
}

const open: PeriodTicketCancelState = {
  locked: false,
  explain: false,
  pending: false,
};

const locked: PeriodTicketCancelState = {
  locked: true,
  explain: true,
  pending: false,
};

const pending: PeriodTicketCancelState = {
  locked: true,
  explain: false,
  pending: true,
};

export const usePeriodTicketCancelState = (
  ticket: IssuedTicketFindResultDTO,
): PeriodTicketCancelState => {
  const fromPeriod = ticketComesFromPeriod(ticket);
  const canLookUpPeriod =
    fromPeriod && canCancelTicket(ticket) && Boolean(ticket.collection_id);
  const { data, isLoading } = useGetCollectionTicketClaims(
    ticket.collection_id ?? '',
    {
      query: { enabled: canLookUpPeriod },
    },
  );

  const claimId = ticket.collection_ticket_claim_id;

  return useMemo(() => {
    if (!fromPeriod) return open;
    if (!canLookUpPeriod) return locked;

    const claim = data?.find((item) => item.id === claimId);
    if (claim) {
      return periodBlocksManualTicketCancel(
        { collection_ticket_claim_id: claimId },
        claim,
      )
        ? locked
        : open;
    }

    if (isLoading) return pending;
    return locked;
  }, [canLookUpPeriod, claimId, data, fromPeriod, isLoading]);
};
